// Fog of war, cloak detection and the "under attack" alert feed.
//
// Three states per tile per player: unexplored / explored / visible. Vision
// obeys StarCraft's height rule -- a ground unit on low ground cannot see onto
// a plateau, which is what makes ramps and high-ground positions matter. Flying
// units see everything within their radius.
//
// TWO RATES, deliberately:
//   * the tile grid is rebuilt at 12 Hz (every second logic frame). It is the
//     most expensive per-tile system in the sim and one frame of latency on a
//     fog edge is imperceptible.
//   * detection and the alert feed run at the full 24 Hz. Both gate things a
//     player feels immediately -- `detected` decides whether combat.js may fire
//     at a cloaked unit this very tick, and an alert for a unit that dies on an
//     odd tick would otherwise be reaped before the next fog pass ever saw it.
//     Both are O(own entities) with no per-tile work, so they are nearly free.
//
// OWNS
//   world.fog[p].vis        Uint8Array, FOG_* per tile
//   world.fog[p].memory     Map id -> remembered enemy structure
//   world.fog[p].alerts     ring of recent "we are being attacked here" pings
//   world.fog[p].version    bumped on every recompute (render layers diff on it)
//   e.detected              cloaked-unit reveal flag
//
// READS ONLY: grid.height, entity positions/sight/flying/detector, e.cloaked,
// e.lastDamagedTick / e.lastAttackerId (written by combat.js).

import { FOG_UNEXPLORED, FOG_EXPLORED, FOG_VISIBLE, TICK_RATE } from './constants.js';
import { UnitDefs, BuildingDefs } from './defs.js';

/** How often the tile grid is recomputed, in logic frames. 2 == 12 Hz. */
export const FOG_INTERVAL = 2;

/** An alert fires at most once per ALERT_REGION-tile cell per ALERT_COOLDOWN. */
const ALERT_REGION = 12;
const ALERT_COOLDOWN = TICK_RATE * 5;
const ALERT_MAX = 12;

// ---------------------------------------------------------------------------
// Vision stamping
//
// The hot loop is O(units x sight area). Walking a flat Int16Array of (dx,dy)
// pairs costs a bounds check per tile; instead cache the half-width of the
// vision disc for each row so an entire row is a clamped, branch-free run.
// ---------------------------------------------------------------------------
const spanCache = new Map();
function spans(r) {
  const key = Math.max(1, Math.round(r * 2));
  let s = spanCache.get(key);
  if (s) return s;
  const rr = key / 2;
  const ri = Math.ceil(rr);
  const half = new Int16Array(2 * ri + 1);
  for (let dy = -ri; dy <= ri; dy++) {
    const v = rr * rr - dy * dy;
    // v === 0 is the single tile at the very top/bottom of the disc. Dropping it
    // (v <= 0) costs nothing in area but makes vision an ellipse that reaches
    // one tile further east/west than north/south, which is visible as a
    // squashed blob on the minimap and is quietly unfair on a rotational map.
    half[dy + ri] = v < 0 ? -1 : Math.floor(Math.sqrt(v));
  }
  s = { ri, half };
  spanCache.set(key, s);
  return s;
}

/** Tile offsets within `r` of the origin. Kept for callers/tests that want the raw disc. */
export function visionDisc(r) {
  const { ri, half } = spans(r);
  const out = [];
  for (let dy = -ri; dy <= ri; dy++) {
    const hw = half[dy + ri];
    for (let dx = -hw; dx <= hw; dx++) out.push(dx, dy);
  }
  return out;
}

/**
 * How far short of its own siege range a sieged unit must see.
 *
 * BW's sieged tank sees 10 and shells 12, so the outer two tiles of its threat
 * range are spotter-only: that gap is the entire reason tank lines want a
 * scan, an observer or a sacrificial marine out front, and the reason "no
 * vision, no fire" is a rule a player can actually feel on flat ground. A def
 * whose siegeSight reaches its siege range makes the tank self-sufficient and
 * silently deletes the dynamic, so the gap is enforced here rather than
 * trusted to the data table.
 */
export const SIEGE_SPOTTER_GAP = 2;

/**
 * Sight radius an entity currently projects. A sieged unit sees whatever its
 * def claims, but never within SIEGE_SPOTTER_GAP of its own siege range; a
 * half-built structure sees less, so walling off does not hand you free vision.
 */
export function sightOf(e) {
  const def = e.kind === 'unit' ? UnitDefs[e.defId] : BuildingDefs[e.defId];
  let sight = e.sight ?? (def ? def.sight : 0);
  if (e.kind === 'unit' && e.sieged && def && def.siegeSight) {
    sight = def.siegeSight;
    const range = def.siegeWeapon ? def.siegeWeapon.range : 0;
    if (range > 0) sight = Math.min(sight, range - SIEGE_SPOTTER_GAP);
  }
  if (e.kind === 'building' && e.constructing) sight = Math.max(4, sight - 3);
  return sight;
}

/**
 * The elevation an entity observes from. Flyers get a level above every terrain
 * level so they are never occluded; ground units get the level of the tile they
 * stand on, which is 0 on a ramp -- you must actually finish walking up.
 */
export function eyeLevel(grid, e) {
  if (e.flying) return 255;
  return grid.heightAt(e.x | 0, e.y | 0);
}

/**
 * The highest terrain level on a grid, memoised per grid object.
 *
 * The occlusion test needs to know when an observer is high enough that nothing
 * can block it. Hard-coding "level 1 sees everything" is true of the current map
 * and silently wrong the day someone authors a third level: every unit on the
 * middle plateau would start seeing through the one above it. Terrain height is
 * baked once at map build time and never mutates, so one scan per grid is free.
 */
/**
 * True if an entity is somewhere the tile grid can actually address.
 *
 * Nothing in a healthy sim ever fails this. It exists because fog reads
 * positions written by every other system, and `NaN | 0` is 0: one unit whose
 * steering divided by a zero-length vector would otherwise stamp a full vision
 * disc into the map's top-left corner every pass, permanently revealing a
 * corner of the map that nobody is standing in. Fog refuses to speak for an
 * entity that is not in coordinate space instead of inventing a position for
 * it, which also keeps the corruption visible to whoever owns movement rather
 * than laundering it into plausible-looking vision.
 */
function onMap(e) {
  return Number.isFinite(e.x) && Number.isFinite(e.y);
}

const maxHeightCache = new WeakMap();
export function maxTerrainLevel(grid) {
  let m = maxHeightCache.get(grid);
  if (m !== undefined) return m;
  m = 0;
  const h = grid.height;
  for (let i = 0; i < h.length; i++) if (h[i] > m) m = h[i];
  maxHeightCache.set(grid, m);
  return m;
}

export function createFog(world, player) {
  const n = world.grid.w * world.grid.h;
  return {
    player,
    vis: new Uint8Array(n),              // FOG_* per tile
    memory: new Map(),                   // id -> { defId, owner, x, y, tx, ty, w, h, hp, maxHp, constructing, tick }
    alerts: [],                          // { x, y, tick, id, kind, defId }
    alertAt: new Map(),                  // region key -> tick of last alert
    version: 0,                          // bumped on every recompute
    dirty: true,
  };
}

export function fogSystem(world) {
  const g = world.grid;
  const w = g.w, h = g.h;
  const height = g.height;
  const topLevel = maxTerrainLevel(g);
  const rebuildTiles = world.tick % FOG_INTERVAL === 0;

  for (let p = 0; p < 2; p++) {
    const f = world.fog[p];

    if (rebuildTiles) {
      const vis = f.vis;

      // Demote last pass's visible tiles to explored. A flat typed-array sweep
      // of 9216 bytes is cheaper and far more predictable than tracking dirty
      // runs, and it is what guarantees a tile never falls back to unexplored.
      for (let i = 0; i < vis.length; i++) if (vis[i] === FOG_VISIBLE) vis[i] = FOG_EXPLORED;

      for (const id of world.byPlayer[p]) {
        const e = world.entities.get(id);
        if (!e || !e.alive || e.hp <= 0) continue;
        if (!onMap(e)) continue;
        const sight = sightOf(e);
        if (!(sight > 0)) continue;

        const ex = e.x | 0, ey = e.y | 0;
        const lvl = eyeLevel(g, e);
        const { ri, half } = spans(sight);
        // Anything at or above the highest terrain level sees without occlusion:
        // flyers, and every unit standing on the top plateau.
        const unoccluded = lvl >= topLevel;

        for (let dy = -ri; dy <= ri; dy++) {
          const ty = ey + dy;
          if (ty < 0 || ty >= h) continue;
          const hw = half[dy + ri];
          if (hw < 0) continue;
          let x0 = ex - hw, x1 = ex + hw;
          if (x0 < 0) x0 = 0;
          if (x1 >= w) x1 = w - 1;
          const row = ty * w;
          if (unoccluded) {
            for (let tx = x0; tx <= x1; tx++) vis[row + tx] = FOG_VISIBLE;
          } else {
            for (let tx = x0; tx <= x1; tx++) {
              const i = row + tx;
              if (height[i] > lvl) continue;             // cannot see up a cliff
              vis[i] = FOG_VISIBLE;
            }
          }
        }
      }

      updateMemory(world, f, g);
      f.version++;
      f.dirty = true;
    }

    collectAlerts(world, f, p);
  }

  updateDetection(world);
}

// ---------------------------------------------------------------------------
// Structure memory
//
// Enemy structures stay painted where they were last seen. The memory is only
// corrected by going back and looking: if the footprint is visible again and the
// building is not there, the ghost is erased. Anything you have not revisited
// keeps lying to you, which is the whole point.
// ---------------------------------------------------------------------------
function updateMemory(world, f, g) {
  const foe = 1 - f.player;
  for (const eid of world.byPlayer[foe]) {
    const e = world.entities.get(eid);
    if (!e || !e.alive || e.kind !== 'building') continue;
    if (!footprintVisible(f.vis, g, e.tx, e.ty, e.w, e.h)) continue;
    f.memory.set(e.id, {
      id: e.id, defId: e.defId, owner: e.owner,
      x: e.x, y: e.y, tx: e.tx, ty: e.ty, w: e.w, h: e.h,
      hp: e.hp, maxHp: e.maxHp, constructing: !!e.constructing,
      tick: world.tick,
    });
  }
  if (!f.memory.size) return;
  for (const [id, m] of f.memory) {
    const e = world.entities.get(id);
    const gone = !e || !e.alive || e.kind !== 'building' || e.owner === f.player;
    if (gone && footprintVisible(f.vis, g, m.tx, m.ty, m.w, m.h)) f.memory.delete(id);
  }
}

/**
 * True if ANY tile of a footprint is currently visible. Testing the centre tile
 * only would hide a 4x4 whose centre sits behind a cliff edge while two of its
 * columns are in plain sight -- the same class of centre-vs-edge mistake that
 * edgeDist() exists to prevent for interaction ranges.
 */
function footprintVisible(vis, g, tx, ty, w, h) {
  const x0 = Math.max(0, tx), x1 = Math.min(g.w - 1, tx + w - 1);
  const y0 = Math.max(0, ty), y1 = Math.min(g.h - 1, ty + h - 1);
  for (let y = y0; y <= y1; y++) {
    const row = y * g.w;
    for (let x = x0; x <= x1; x++) if (vis[row + x] === FOG_VISIBLE) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// "Under attack" alerts
//
// Derived from e.lastDamagedTick rather than the event stream so that whoever
// owns event draining cannot starve the minimap. Rate-limited per map region
// the way BW rate-limits the voice line, otherwise a single siege volley pings
// twenty times.
//
// Runs at the full 24 Hz. combat.js writes the damage earlier in the same tick
// and reap() deletes anything that hit zero at the end of it, so a unit killed
// outright on an odd frame exists only during that one fog call: collecting at
// 12 Hz would silently swallow the ping for exactly the losses that matter most.
// The window is kept a couple of frames wide so an out-of-band fogSystem() call
// (the harness, the constructor) still catches recent damage; re-reporting the
// same hit is harmless because the region cooldown absorbs it.
// ---------------------------------------------------------------------------
function collectAlerts(world, f, p) {
  const cutoff = world.tick - FOG_INTERVAL;
  for (const id of world.byPlayer[p]) {
    const e = world.entities.get(id);
    if (!e || !e.alive || !onMap(e)) continue;
    if (!(e.lastDamagedTick >= cutoff)) continue;
    const attacker = e.lastAttackerId != null ? world.entities.get(e.lastAttackerId) : null;
    if (attacker && attacker.owner === p) continue;          // friendly splash is not an alarm
    const key = (((e.y / ALERT_REGION) | 0) << 8) | ((e.x / ALERT_REGION) | 0);
    const last = f.alertAt.get(key);
    if (last !== undefined && world.tick - last < ALERT_COOLDOWN) continue;
    f.alertAt.set(key, world.tick);
    f.alerts.push({ x: e.x, y: e.y, tick: world.tick, id: e.id, kind: e.kind, defId: e.defId });
    if (f.alerts.length > ALERT_MAX) f.alerts.shift();
  }
  if (f.alertAt.size > 64) {
    for (const [k, t] of f.alertAt) if (world.tick - t >= ALERT_COOLDOWN) f.alertAt.delete(k);
  }
}

/** Take and clear the pending alerts for `player`. The minimap owns the flashing. */
export function drainAlerts(world, player) {
  const f = world.fog[player];
  if (!f.alerts.length) return [];
  const out = f.alerts;
  f.alerts = [];
  return out;
}

// ---------------------------------------------------------------------------
// Cloak detection
//
// `e.detected` is a single flag, not per-player, and that is exactly right for a
// two-player match: the only observer whose answer can differ from the owner's
// is the one enemy, and canSee() short-circuits on ownership before it is read.
// Detection additionally requires plain vision of the tile, so a turret at the
// bottom of a cliff does not magically reveal a wraith above it.
//
// Runs at the full 24 Hz, unlike the tile grid: `detected` is what combat.js
// consults to decide whether it may fire this tick, so resolving it at 12 Hz
// would give a cloaked unit up to 83 ms of grace after flying into a detector's
// range. The scan is O(units) with no per-tile work and exits immediately when
// nobody on the map is cloaked, which is almost every tick of almost every game.
// ---------------------------------------------------------------------------
const MAX_DETECT_RANGE = (() => {
  let r = 0;
  for (const d of Object.values(UnitDefs)) if (d.detector) r = Math.max(r, d.sight);
  for (const d of Object.values(BuildingDefs)) if (d.detector) r = Math.max(r, d.sight);
  return r;
})();

const _det = [];
function updateDetection(world) {
  let cloaked = null;
  for (let p = 0; p < 2; p++) {
    for (const id of world.byPlayer[p]) {
      const u = world.entities.get(id);
      if (!u || !u.alive || u.kind !== 'unit') continue;
      if (u.cloaked) { u.detected = false; (cloaked ||= []).push(u); }
      else u.detected = true;
    }
  }
  if (!cloaked) return;
  for (const u of cloaked) {
    const foe = 1 - u.owner;
    if (!isVisible(world, foe, u.x, u.y)) continue;         // no vision, no detection
    world.queryRadius(u.x, u.y, MAX_DETECT_RANGE + 1.5, _det);
    for (let i = 0; i < _det.length; i++) {
      const d = _det[i];
      if (d.owner !== foe || !d.alive || !d.detector) continue;
      if (d.kind === 'building' && d.constructing) continue;
      const r = sightOf(d);
      const dx = d.x - u.x, dy = d.y - u.y;
      if (dx * dx + dy * dy <= r * r) { u.detected = true; break; }
    }
  }
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------
export function visStateAt(world, player, x, y) {
  const g = world.grid;
  const xi = Math.floor(x), yi = Math.floor(y);
  // Positive test, not `xi < 0 || xi >= g.w`: NaN fails every comparison, so the
  // negated form waves it through to vis[NaN], which is `undefined`. That reads
  // as false from isVisible() but as TRUE from isExplored(), i.e. a unit whose
  // position went non-finite would count as having explored nowhere and
  // everywhere at once. Off the map is unexplored; so is nowhere at all.
  if (!(xi >= 0 && yi >= 0 && xi < g.w && yi < g.h)) return FOG_UNEXPLORED;
  return world.fog[player].vis[yi * g.w + xi];
}
export function isVisible(world, player, x, y) {
  return visStateAt(world, player, x, y) === FOG_VISIBLE;
}
export function isExplored(world, player, x, y) {
  return visStateAt(world, player, x, y) !== FOG_UNEXPLORED;
}

/** True if any tile the entity physically covers is currently visible to `player`. */
export function entityVisible(world, player, e) {
  const g = world.grid;
  const vis = world.fog[player].vis;
  if (e.tx === undefined) return isVisible(world, player, e.x, e.y);
  const cx = e.x | 0, cy = e.y | 0;
  // `NaN | 0` is 0, so the centre-tile shortcut would report an off-coordinate
  // entity as visible whenever the map's corner tile happens to be lit.
  if (onMap(e) && cx >= 0 && cy >= 0 && cx < g.w && cy < g.h && vis[cy * g.w + cx] === FOG_VISIBLE) return true;
  return footprintVisible(vis, g, e.tx, e.ty, e.w, e.h);
}

/** Can `player` legally see and therefore target entity `e`? */
export function canSee(world, player, e) {
  if (e.owner === player) return true;
  if (!entityVisible(world, player, e)) return false;
  if (e.cloaked && !e.detected) return false;
  return true;
}

/** Remembered enemy structures for `player`, for the minimap and the AI. */
export function rememberedStructures(world, player) {
  return [...world.fog[player].memory.values()];
}
